import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  signal,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { SelectModule } from '@openng/optimus-ui/select';
import { OrganizationsService } from '../../services/organizations.service';
import {
  BillingPeriod,
  CreateOrganizationRequest,
  CreatedOrganization,
} from '../../models/organization.types';
import { ErrorBannerComponent } from '../shared/error-banner.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Create an organisation on a customer's behalf, with the plan they were sold.
 *
 * Its own component rather than another dialog inside the organisations table: the table screen is
 * already large, and this form owns state - a plan list, a submission in flight, field errors - that
 * has nothing to do with listing. The parent only opens it and hears back what was created.
 */
@Component({
  selector: 'app-create-organization-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ErrorBannerComponent,
  ],
  templateUrl: './create-organization-dialog.component.html',
  styleUrl: './create-organization-dialog.component.scss',
})
export class CreateOrganizationDialogComponent {
  private fb = inject(FormBuilder);
  private orgService = inject(OrganizationsService);

  readonly visible = model.required<boolean>();
  readonly created = output<CreatedOrganization>();

  protected plans = this.orgService.pricingPlans;
  protected submitting = signal(false);
  protected submitError = signal<string | null>(null);

  protected billingPeriods: { label: string; value: BillingPeriod }[] = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Annually', value: 'annually' },
  ];

  protected form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    adminEmail: ['', [Validators.required, Validators.email]],
    pricingPlanId: this.fb.control<number | null>(null, Validators.required),
    seats: [1, [Validators.required, Validators.min(1)]],
    billingPeriod: ['monthly' as BillingPeriod, Validators.required],
    registrationCode: [''],
    address: [''],
  });

  /** Shown next to the plan picker so the operator can see what they are about to sell. */
  protected selectedPlanPrice = computed(() => {
    const planId = this.selectedPlanId();
    const plan = this.plans().find((candidate) => candidate.id === planId);
    if (!plan) return null;

    const period = this.selectedBillingPeriod();
    const perSeat = period === 'annually' ? plan.pricePerSeatAnnually : plan.pricePerSeatMonthly;

    return { perSeat, currency: plan.currency, period };
  });

  // The form is not a signal, so the two values the template derives from are mirrored into signals
  // on every value change. Reading `form.value` from a `computed` would never recompute.
  private selectedPlanId = signal<number | null>(null);
  private selectedBillingPeriod = signal<BillingPeriod>('monthly');

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.selectedPlanId.set(value.pricingPlanId ?? null);
      this.selectedBillingPeriod.set(value.billingPeriod ?? 'monthly');
    });

    // Loaded when the dialog is first opened rather than on page load: an operator creates an
    // organisation rarely, and everyone else visiting the list pays nothing for this.
    effect(() => {
      if (this.visible() && this.plans().length === 0) {
        this.orgService.loadPricingPlans();
      }
    });
  }

  protected invalid(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  protected submit() {
    if (this.submitting()) return;

    // Trimmed before the validity check, not after: an address pasted with a trailing space is a
    // normal thing to do, and `Validators.email` rejects it outright. Otherwise the operator is
    // told their own customer's email is invalid and cannot see why.
    this.trimTextFields();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const body: CreateOrganizationRequest = {
      name: value.name,
      adminEmail: value.adminEmail,
      // Guarded by the required validator above; the cast keeps the null out of the request type.
      pricingPlanId: value.pricingPlanId as number,
      seats: value.seats,
      billingPeriod: value.billingPeriod,
    };

    if (value.registrationCode) body.registrationCode = value.registrationCode;
    if (value.address) body.address = value.address;

    this.submitting.set(true);
    this.submitError.set(null);

    this.orgService.createOrganization(body).subscribe({
      next: (organisation) => {
        this.submitting.set(false);
        this.created.emit(organisation);
        this.close();
      },
      error: (error: HttpErrorResponse) => {
        this.submitting.set(false);
        this.submitError.set(this.describe(error));
      },
    });
  }

  private trimTextFields() {
    const value = this.form.getRawValue();
    this.form.patchValue({
      name: value.name.trim(),
      adminEmail: value.adminEmail.trim(),
      registrationCode: value.registrationCode.trim(),
      address: value.address.trim(),
    });
  }

  protected close() {
    this.visible.set(false);
    this.submitError.set(null);
    this.form.reset({ seats: 1, billingPeriod: 'monthly' });
  }

  /**
   * The API distinguishes the two mistakes an operator actually makes - a name already in use and a
   * plan that no longer exists - so pass its own wording through rather than flattening both into
   * "something went wrong". Nest sends validation failures as an array of messages.
   */
  private describe(error: HttpErrorResponse): string {
    const body = error.error as { message?: string | string[] } | null;
    const apiMessage = Array.isArray(body?.message) ? body?.message.join(' ') : body?.message;

    if (apiMessage) return apiMessage;
    if (error.status === 409) return 'An organisation with that name already exists.';
    if (error.status === 400) return 'Some of the values in this form were rejected.';
    if (error.status === 0) return 'The API could not be reached.';

    return 'Creating the organisation failed.';
  }
}
